import React, { useMemo } from "react";
import { Space, Tooltip } from "antd";
import { CheckCircleOutlined, HeartOutlined } from "@ant-design/icons";
import type { TreeNodeData } from "@ms/shared-components/comps/CompsTree";
import type { DescriptionItem } from "@ms/shared-components/comps/CompsDescription";
import { getIconMap } from "@ms/shared-components";
import { ServiceRecord, SERVICE_STATUS_MAP } from "../service-apis/serviceTypes";
import { actionNameMap } from "../config/modalPermissionConfig";
import { EMPTY_TOKEN_CONFIG, buildTokenDescriptionItems, buildSeparatorItem } from "../config/tabTokenConfig";
import { buildServiceDescriptionItems } from "../config/tabBaseConfig";

/**
 * 服务数据转换相关的 Hook
 * 负责将原始数据转换为 UI 组件所需的数据格式
 */
export const useServiceDataTransformers = (
  serviceList: ServiceRecord[],
  permissions: any[],
  serviceDetail: any,
  serviceTokens: any[]
) => {
  // 构建服务列表数据，显示运行状态和健康状态
  const serviceListData = useMemo(() => {
    // 按菜单顺序优先排序，其次按名称排序，保证列表与菜单一致
    const sortedServices = [...serviceList].sort((a, b) => {
      const orderA = (a.menuOrder ?? (a as any).menu_order ?? 0) as number;
      const orderB = (b.menuOrder ?? (b as any).menu_order ?? 0) as number;
      if (orderA !== orderB) {
        return orderA - orderB;
      }
      const nameA = (a.displayName || a.display_name || a.name || a.service_name || "") as string;
      const nameB = (b.displayName || b.display_name || b.name || b.service_name || "") as string;
      return nameA.localeCompare(nameB);
    });

    return sortedServices.map((service) => {
      const serviceName = service.name || service.service_name || service.key;
      const displayName = service.displayName || service.display_name || serviceName;

      // 构建左侧内容：中文名 + 英文名（如果英文名与中文名不同）
      const leftContent = serviceName && serviceName !== displayName ? `${displayName} (${serviceName})` : displayName;

      // 运行状态
      const statusInfo = SERVICE_STATUS_MAP[service.status as keyof typeof SERVICE_STATUS_MAP];
      const statusText = statusInfo?.text || "未知";
      const isStatusNormal = statusInfo?.color === "success";
      const statusColor = isStatusNormal ? "#52c41a" : statusInfo?.color === "error" ? "#ff4d4f" : "#faad14";

      // 健康状态
      const healthStatus = service.healthStatus !== undefined ? service.healthStatus : null;

      // 右侧内容：运行状态（圆圈对勾图标，绿色表示正常/可用）+ 健康状态（心型图标，绿色表示健康）
      const rightContent = (
        <Space size="small">
          <Tooltip title={statusText}>
            <CheckCircleOutlined style={{ color: statusColor, fontSize: 16 }} />
          </Tooltip>
          {healthStatus !== null && (
            <Tooltip title={healthStatus ? "健康" : "异常"}>
              <HeartOutlined style={{ color: healthStatus ? "#52c41a" : "#ff4d4f", fontSize: 16 }} />
            </Tooltip>
          )}
        </Space>
      );

      return {
        id: service.id,
        leftContent,
        rightContent,
        serviceName: serviceName,
        service,
      };
    });
  }, [serviceList]);

  // 构建服务树数据：按服务类型分为系统、后台、中台、前台四个分组
  const serviceTreeData = useMemo<TreeNodeData[]>(() => {
    if (!serviceList || serviceList.length === 0) {
      return [];
    }

    const groups: { key: string; title: string; typeValue: number }[] = [
      { key: "system", title: "系统服务", typeValue: 0 },
      { key: "backend", title: "后台服务", typeValue: 1 },
      { key: "middle", title: "中台服务", typeValue: 2 },
      { key: "frontend", title: "前台服务", typeValue: 3 },
    ];

    const iconMap = getIconMap();

    const getServiceType = (service: ServiceRecord): number => {
      if (typeof service.serviceType === "number") return service.serviceType;
      if (typeof (service as any).service_type === "number") return (service as any).service_type;
      if (service.isSystemService) return 0;
      return 1;
    };

    const categorized: Record<string, ServiceRecord[]> = {
      system: [],
      backend: [],
      middle: [],
      frontend: [],
    };

    serviceList.forEach((service) => {
      const rawType = getServiceType(service);
      const nameLike =
        (service.name ||
          service.displayName ||
          (service as any).display_name ||
          service.service_name ||
          "") as string;
      const isRealSystemService = nameLike === "微服务管理";
      const type =
        rawType === 0 && !isRealSystemService
          ? 1 // 除「微服务管理」外，其它服务即使标记为 0 也按后台服务处理
          : rawType;

      const targetGroup =
        type === 0 ? "system" : type === 2 ? "middle" : type === 3 ? "frontend" : "backend";
      categorized[targetGroup].push(service);
    });

    const result: TreeNodeData[] = [];

    groups.forEach((group) => {
      const services = categorized[group.key] || [];
      if (services.length === 0) return;

      const sorted = [...services].sort((a, b) => {
        const orderA = (a.menuOrder ?? (a as any).menu_order ?? 0) as number;
        const orderB = (b.menuOrder ?? (b as any).menu_order ?? 0) as number;
        if (orderA !== orderB) return orderA - orderB;
        const nameA = (a.displayName || a.display_name || a.name || a.service_name || "") as string;
        const nameB = (b.displayName || b.display_name || b.name || b.service_name || "") as string;
        return nameA.localeCompare(nameB);
      });

      const children: TreeNodeData[] = sorted.map((service) => {
        const serviceName = service.name || service.service_name || service.key;
        const displayName = service.displayName || service.display_name || serviceName;
        const iconName = service.icon || service.icon_name;
        const IconComponent = iconName ? iconMap[iconName] : undefined;
        return {
          key: service.id,
          title:
            serviceName && serviceName !== displayName
              ? `${displayName} (${serviceName})`
              : displayName,
          isLeaf: true,
          service,
          ...(IconComponent
            ? { icon: React.createElement(IconComponent, { style: { fontSize: 14 } }) }
            : {}),
        };
      });

      result.push({
        key: `group-${group.key}`,
        title: `${group.title}（${children.length}）`,
        children,
      });
    });

    return result;
  }, [serviceList]);

  // 将权限数据转换为树形结构
  const permissionTreeData = useMemo((): TreeNodeData[] => {
    if (!permissions || permissions.length === 0) {
      return [];
    }

    const groupedByService = permissions.reduce(
      (acc, permission) => {
        const codeParts = permission.code.split(":");
        const serviceKey = (permission.service || permission.service_name || codeParts[0] || "").toLowerCase();
        if (!serviceKey) {
          return acc;
        }
        if (!acc[serviceKey]) {
          acc[serviceKey] = [];
        }
        acc[serviceKey].push(permission);
        return acc;
      },
      {} as Record<string, any[]>
    );

    const result: TreeNodeData[] = [];

    Object.entries(groupedByService).forEach(([serviceKey, servicePermissions]) => {
      const permissionsForService = servicePermissions as any[];
      const firstPermission = permissionsForService[0] || {};
      const displayName =
        firstPermission.serviceName || firstPermission.service_name || firstPermission.service || serviceKey;
      const serviceNode: TreeNodeData = {
        key: `service-${serviceKey}`,
        title: `${displayName} (${serviceKey})`,
        children: [],
      };

      const groupedByModule = permissionsForService.reduce(
        (acc: Record<string, any[]>, permission: any) => {
          const parts = permission.code.split(":");
          let module: string;

          if (permission.code === "*") {
            module = "global";
          } else if (parts.length >= 2) {
            module = parts[1];
          } else {
            module = permission.service_name || permission.serviceName || "unknown";
          }

          if (!acc[module]) {
            acc[module] = [];
          }
          acc[module].push(permission);
          return acc;
        },
        {} as Record<string, any[]>
      );

      Object.entries(groupedByModule).forEach(([module, modulePermissions]) => {
        const permissionsForModule = modulePermissions as any[];
        const moduleNode: TreeNodeData = {
          key: `module-${serviceKey}-${module}`,
          title: module,
          children: [],
        };

        permissionsForModule.forEach((permission: any) => {
          const parts = permission.code.split(":");
          const action = parts.length >= 3 ? parts[2] : permission.action || "unknown";
          const actionDisplayName = actionNameMap[action] || action;

          moduleNode.children?.push({
            key: permission.id,
            title: `${actionDisplayName} (${permission.code})`,
            isLeaf: true,
          });
        });

        serviceNode.children?.push(moduleNode);
      });

      result.push(serviceNode);
    });

    return result;
  }, [permissions]);

  // 构建基本信息描述数据
  const basicInfoData = useMemo((): DescriptionItem[] => {
    return buildServiceDescriptionItems(serviceDetail);
  }, [serviceDetail]);

  // 构建服务授权描述数据（按照service_token表结构显示）
  const serviceAuthData = useMemo((): DescriptionItem[] => {
    if (!serviceTokens || serviceTokens.length === 0) {
      return [EMPTY_TOKEN_CONFIG];
    }

    // 将每个token的所有字段展开为描述项
    const items: DescriptionItem[] = [];

    serviceTokens.forEach((token, index) => {
      // 使用配置构建token字段描述项
      const tokenItems = buildTokenDescriptionItems(token, index);
      items.push(...tokenItems);

      // 添加分隔线（除了最后一个token）
      if (index < serviceTokens.length - 1) {
        items.push(buildSeparatorItem(index));
      }
    });

    return items;
  }, [serviceTokens]);

  return {
    serviceListData,
    serviceTreeData,
    permissionTreeData,
    basicInfoData,
    serviceAuthData,
  };
};
